import React, { useEffect, useRef, useState } from "react";
import { FaceMesh } from "@mediapipe/face_mesh";
import { Camera } from "@mediapipe/camera_utils";
import { motion } from "framer-motion";
import Navbar from "./Navbar";

const RPPGMonitor = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const faceMeshRef = useRef(null);
  const cameraRef = useRef(null);
  const animationRef = useRef(null);
  const firstVitalsRef = useRef(null);
  const lastVitalsRef = useRef(null);

  const [isRunning, setIsRunning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [heartRate, setHeartRate] = useState("--");
  const [spo2, setSpo2] = useState("--");
  const [bloodPressure, setBloodPressure] = useState("-- / --");
  const [confidence, setConfidence] = useState("--");

  
  
      

  const sendVitalsToAPI = async (data) => {
    try {
      await fetch("https://www.careplus.gopillz.com/save_vitals", {
        method: "POST",
        headers: { "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        Accept: "*/*",
      },
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error("Error sending vitals:", error);
    }
  };

  useEffect(() => {
    if (!isRunning) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const faceMesh = new FaceMesh({
      locateFile: (file) =>
        `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
    });
    faceMeshRef.current = faceMesh;

    faceMesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: true,
      minDetectionConfidence: 0.6,
      minTrackingConfidence: 0.6,
    });

    faceMesh.onResults(onResults);

    async function startCamera() {
      try {
        setIsLoading(true);
        const camera = new Camera(video, {
          onFrame: async () => {
            if (faceMeshRef.current) {
              await faceMeshRef.current.send({ image: video });
            } 
          },
          width: 640,
          height: 480,
        });
        cameraRef.current = camera;
        await camera.start();
        setIsLoading(false);
      } catch (err) {
        console.error("Camera error:", err);
        alert("Unable to access camera. Please allow permissions.");
        setIsRunning(false);
        setIsLoading(false);
      }
    }

    startCamera();

    function onResults(results) { 
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
    
      ctx.save();
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (results.image) {
        ctx.drawImage(results.image, 0, 0, canvas.width, canvas.height);
      }
    
      if (results.multiFaceLandmarks?.length > 0) {
        const landmarks = results.multiFaceLandmarks[0];
        const leftCheek = landmarks[234];
        const rightCheek = landmarks[454];
        ctx.fillStyle = "rgba(0,255,0,0.4)";
        [leftCheek, rightCheek].forEach((p) => {
          ctx.beginPath();
          ctx.arc(p.x * canvas.width, p.y * canvas.height, 5, 0, 2 * Math.PI);
          ctx.fill();
        });
    
        // Simulated vitals
        const hr = 70 + Math.floor(Math.random() * 8);
        const sp = 97 + Math.floor(Math.random() * 2);
        const bpSys = 115 + Math.floor(Math.random() * 10);
        const bpDia = 75 + Math.floor(Math.random() * 5);
        const conf = (0.8 + Math.random() * 0.2).toFixed(2);
    
        setHeartRate(hr);
        setSpo2(sp);
        setBloodPressure(`${bpSys}/${bpDia}`);
        setConfidence(conf);
    
        // Create vitals object
        const vitals = {
          heart_rate: hr,
          spo2: sp,
          blood_pressure: `${bpSys}/${bpDia}`,
          confidence: parseFloat(conf),
        };
    
        // ⬇ Save LAST value continuously
        lastVitalsRef.current = vitals;
    
        // ⬇ Send FIRST value ONE TIME only
        if (!firstVitalsRef.current) {
          firstVitalsRef.current = vitals;
          sendVitalsToAPI(vitals);  // send first reading
        }
      }
    
      ctx.restore();
    
      if (isRunning) {
        animationRef.current = requestAnimationFrame(() =>
          faceMesh.send({ image: video })
        );
      }
    }
    

    // ✅ Cleanup when stopped
    return () => {
      if (cameraRef.current) cameraRef.current.stop();
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      faceMeshRef.current = null;
    };
  }, [isRunning]);

  const handleStart = () => setIsRunning(true);

  const handleStop = () => {
    setIsRunning(false);
  
    // Stop camera + animation
    if (cameraRef.current) cameraRef.current.stop();
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    faceMeshRef.current = null;
  
    // ⬇ SEND LAST VALUE BEFORE RESETTING UI
    if (lastVitalsRef.current) {
      sendVitalsToAPI({
        type: "last",
        ...lastVitalsRef.current,
      });
    }
  
    // Reset UI values AFTER sending last data
    setHeartRate("--");
    setSpo2("--");
    setBloodPressure("-- / --");
    setConfidence("--");
  
    // Clear values for next session
    firstVitalsRef.current = null;
    lastVitalsRef.current = null;
  };
  

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2, duration: 0.5 },
    }),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-white to-slate-200 p-4">
      <Navbar />

    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-[#020617] via-[#0f172a] to-[#1e293b] text-white p-6">
      <motion.h1
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-4xl font-extrabold mb-6 bg-gradient-to-r from-emerald-400 to-teal-500 bg-clip-text text-transparent"
      >
        Real-Time Vital Signs Monitor
      </motion.h1>

      {!isRunning ? (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleStart}
          className="px-8 py-3 text-lg font-semibold rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 shadow-lg"
        >
          Start Monitoring
        </motion.button>
      ) : (
        <>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="relative rounded-3xl overflow-hidden border border-emerald-500/30 shadow-[0_0_25px_rgba(16,185,129,0.3)] backdrop-blur-xl"
          >
            <video ref={videoRef} className="hidden" width="640" height="480" />
            <canvas ref={canvasRef} width="640" height="480" />
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-lg text-emerald-400">
                Initializing Camera...
              </div>
            )}
          </motion.div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleStop}
            className="mt-6 px-8 py-3 text-lg font-semibold rounded-2xl bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-400 hover:to-pink-500 shadow-lg"
          >
            Stop Monitoring
          </motion.button>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-10">
            {[
              { label: "❤️ Heart Rate", value: `${heartRate} bpm`, color: "from-pink-500 to-red-500" },
              { label: "🌬️ SpO₂", value: `${spo2} %`, color: "from-sky-400 to-blue-500" },
              { label: "💉 Blood Pressure", value: bloodPressure, color: "from-purple-500 to-fuchsia-500" },
              { label: "📊 Confidence", value: confidence, color: "from-amber-400 to-yellow-500" },
            ].map((stat, i) => (
              <motion.div
                key={i}
                custom={i}
                initial="hidden"
                animate="visible"
                variants={cardVariants}
                className="p-6 rounded-2xl bg-gradient-to-br from-gray-900/70 to-gray-800/50 shadow-lg border border-gray-700/50 backdrop-blur-xl"
              >
                <p className="text-base text-gray-400 mb-2">{stat.label}</p>
                <p
                  className={`text-4xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}
                >
                  {stat.value}
                </p>
              </motion.div>
            ))}
          </div>
        </>
      )}
    </div>
    </div>
  );
};

export default RPPGMonitor;
